import { useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleDashed, ShieldCheck, SprayCan, Users, Apple, ListChecks } from 'lucide-react';
import { Tabs, type TabItem } from '../ui/Tabs';
import { formatLocalDateInTz } from '../../utils/timezone';
import type {
  AutoChecks, CheckState, ComplianceReport, HarvestSection, PeopleSection, SpraySection, WhsSection, WeatherReading,
} from '../../types/compliance';

/**
 * The season report as a page: the evidence summary, then one tab per section.
 * Shared by the org's preview (/compliance) and the auditor portal
 * (/audit/:token), so an org sees exactly what its auditor will.
 *
 * Wide tables sit in `overflow-x-auto` so they scroll on a phone; the spray
 * register is cards, because that is the table an auditor actually reads
 * line by line and a sideways-scrolling row of nine columns is not readable.
 */

type TabKey = 'summary' | 'spray' | 'people' | 'whs' | 'harvest';

const CHECK_LABELS: Record<string, string> = {
  spray_records_complete: 'Spray records complete',
  spray_justification: 'Every product names its target',
  label_compliance: 'Applied within label rates',
  whp_respected: 'Withholding periods respected at harvest',
  whp_overrides_decided: 'Withholding-period decisions made',
  operator_competency: 'Operators held a current credential',
  sprayer_calibration: 'Sprayers calibrated in the last 12 months',
  chemical_stock: 'Chemical stock counted',
  batch_traceability: 'Batch numbers recorded',
  training_records: 'Induction sign-offs recorded',
  policies_acknowledged: 'Induction pack acknowledged',
  documents_reviewed: 'Documents within review date',
  risk_assessments_current: 'Risk assessments current',
  incidents_managed: 'Incidents followed up',
  corrective_actions: 'Corrective actions on time',
  internal_audit: 'Internal audit in the last 12 months',
  traceability: 'Bins traceable to a planting',
};

export function CheckBadge({ state }: { state: CheckState }) {
  if (state === 'met') return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"><CheckCircle2 className="h-3.5 w-3.5" />Met</span>;
  if (state === 'gap') return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"><AlertTriangle className="h-3.5 w-3.5" />Gap</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600"><CircleDashed className="h-3.5 w-3.5" />No data</span>;
}

function Heading({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 mb-2 text-sm font-semibold text-neutral-900 first:mt-0">{children}</h3>;
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-neutral-500">{children}</p>;
}

function Table({ head, rows, highlight }: { head: string[]; rows: ReactNode[][]; highlight?: (i: number) => boolean }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-200">
      <table className="min-w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
          <tr>{head.map(h => <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {rows.map((r, i) => (
            <tr key={i} className={highlight?.(i) ? 'bg-red-50' : undefined}>
              {r.map((c, j) => <td key={j} className="px-3 py-2 align-top text-neutral-800">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useDate(tz: string) {
  return (d: string | null | undefined) => (d ? formatLocalDateInTz(d.slice(0, 10), tz) : '—');
}

const fmt = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : String(Math.round(Number(v) * 10 ** digits) / 10 ** digits);

function weatherText(w: WeatherReading | null) {
  if (!w) return 'Not recorded';
  const bits = [`${fmt(w.temp_c)}°C`, `${fmt(w.wind_kph)} km/h${w.wind_dir ? ` ${w.wind_dir}` : ''}`];
  if (w.humidity != null) bits.push(`${fmt(w.humidity, 0)}% RH`);
  return `${bits.join(', ')}${w.source ? ` · ${w.source === 'station' ? 'station' : 'forecast grid'}` : ''}`;
}

const CREDENTIAL_TEXT = { current: 'current credential', not_current: 'no current credential on the day', none: 'no credential on file' };

function SprayView({ s, tz }: { s: SpraySection; tz: string }) {
  const date = useDate(tz);
  return (
    <div>
      <Heading>Spray applications ({s.applications.length})</Heading>
      {s.cancelled_count > 0 && <p className="mb-2 text-xs text-neutral-500">{s.cancelled_count} cancelled before application, not listed.</p>}
      {s.applications.length === 0 ? <Empty>No sprays applied in the period.</Empty> : (
        <ul className="space-y-3">
          {s.applications.map(a => {
            const flagged = a.operator_credential !== 'current' || a.label_compliance === 'above_label';
            return (
              <li key={a.id} className={`rounded-lg border p-3 ${flagged ? 'border-red-200 bg-red-50' : 'border-neutral-200'}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="min-w-0 break-words font-medium text-neutral-900">{date(a.applied_date)} · {a.targets.map(t => t.name).join(', ') || a.title}</p>
                  <span className="text-xs text-neutral-500">{a.reference_number}{a.status !== 'completed' ? ` · ${a.status.replace('_', ' ')}` : ''}</span>
                </div>
                {a.quarantine && (
                  <p className="mt-1 text-xs text-neutral-600 break-words">
                    {`Quarantine treatment ${a.quarantine.code}, load ${a.quarantine.load_number}`}
                    {a.quarantine.validated_by ? `, validated by ${a.quarantine.validated_by}` : ', not validated'}
                  </p>
                )}
                <ul className="mt-2 space-y-0.5 text-sm">
                  {a.mix.map((m, i) => (
                    <li key={i} className="break-words">
                      <span className="font-medium">{m.product_name}</span>
                      {m.apvma_number && <span className="text-neutral-500"> [{m.apvma_number}]</span>}
                      {' '}{fmt(m.rate, 3)} {m.rate_unit}
                      {m.target_pest ? <span className="text-neutral-600"> — {m.target_pest}</span> : <span className="text-red-700"> — no target recorded</span>}
                    </li>
                  ))}
                </ul>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-neutral-600 sm:grid-cols-2">
                  <div className="min-w-0 break-words"><dt className="inline font-medium text-neutral-700">Operator: </dt><dd className="inline">{a.operator ?? '—'} <span className={a.operator_credential === 'current' ? '' : 'font-medium text-red-700'}>({CREDENTIAL_TEXT[a.operator_credential]})</span></dd></div>
                  <div className="min-w-0 break-words"><dt className="inline font-medium text-neutral-700">Equipment: </dt><dd className="inline">{a.equipment ?? '—'}{a.calibration ? ` · calibrated ${date(a.calibration.date)}, ${fmt(a.calibration.output_lpha, 0)} L/ha` : ' · no calibration recorded'}</dd></div>
                  <div><dt className="inline font-medium text-neutral-700">Area / water: </dt><dd className="inline">{fmt(a.actual_area_ha ?? a.total_target_area_ha, 2)} ha{a.actual_area_source ? ` (${a.actual_area_source})` : ''} · {fmt(a.total_water_l, 0)} L</dd></div>
                  <div><dt className="inline font-medium text-neutral-700">WHP: </dt><dd className="inline">{a.whp_days ?? '—'} days, clear {date(a.whp_clear_date)}{a.whp_override_status && a.whp_override_status !== 'none' ? ` · override ${a.whp_override_status}${a.whp_override_decided_by ? ` by ${a.whp_override_decided_by}` : ''}` : ''}</dd></div>
                  <div className="min-w-0 break-words"><dt className="inline font-medium text-neutral-700">Weather at start: </dt><dd className="inline">{weatherText(a.weather_start)}</dd></div>
                  <div className="min-w-0 break-words"><dt className="inline font-medium text-neutral-700">At sign-off: </dt><dd className="inline">{weatherText(a.weather_end)}</dd></div>
                  <div><dt className="inline font-medium text-neutral-700">Label: </dt><dd className="inline">{a.label_compliance?.replace('_', ' ') ?? '—'}</dd></div>
                  <div className="min-w-0 break-words"><dt className="inline font-medium text-neutral-700">Batches: </dt><dd className="inline">{a.batches.length ? a.batches.map(b => `${b.product_name} ${b.batch_number}`).join('; ') : 'none recorded'}</dd></div>
                  {a.signed_off_by && <div><dt className="inline font-medium text-neutral-700">Signed off: </dt><dd className="inline">{a.signed_off_by}</dd></div>}
                </dl>
              </li>
            );
          })}
        </ul>
      )}

      <Heading>Withholding periods against harvest</Heading>
      {s.whp_harvest.areas.length === 0 ? <Empty>No bins picked in the period.</Empty> : (
        <Table
          head={['Planting', 'Cultivar', 'Picked', 'Bins', 'WHP clear before first pick', 'Picked inside WHP']}
          highlight={i => s.whp_harvest.areas[i].bins_within_whp > 0}
          rows={s.whp_harvest.areas.map(a => [
            [a.farm_name, a.block_name, a.area_name].filter(Boolean).join(' › '), a.cultivar_name ?? '—',
            `${date(a.first_pick)} – ${date(a.last_pick)}`, a.bins, date(a.latest_clear_before_first_pick),
            a.bins_within_whp ? `${a.bins_within_whp}: ${a.conflicts.map(c => `${c.reference_number ?? c.title} (clear ${date(c.whp_clear_date)})`).join('; ')}` : 'None',
          ])}
        />
      )}
      {s.whp_harvest.bins_without_planting > 0 && <p className="mt-1 text-xs text-red-700">{s.whp_harvest.bins_without_planting} bins have no planting and could not be checked.</p>}

      <Heading>Operators and credentials</Heading>
      {s.operators.length === 0 ? <Empty>No operators in the period.</Empty> : (
        <Table
          head={['Operator', 'Sprays', 'Without current credential', 'Credentials']}
          highlight={i => s.operators[i].sprays_without_current_credential > 0}
          rows={s.operators.map(o => [o.name, o.sprays, o.sprays_without_current_credential,
            o.credentials.length ? o.credentials.map(c => `${c.type} ${c.number ?? ''} (to ${date(c.expiry_date)}${c.status_at_end === 'expired' ? ', expired' : ''})`).join('; ') : 'None on file'])}
        />
      )}

      <Heading>Sprayer calibration</Heading>
      {s.calibrations.length === 0 ? <Empty>No sprayer used or calibrated in the period.</Empty> : (
        <Table
          head={['Equipment', 'Sprays', 'Calibrations in period', 'Last calibration']}
          rows={s.calibrations.map(e => [e.equipment, e.sprays_in_period, e.calibrations_in_period,
            e.last_calibration ? `${date(e.last_calibration.date)} · ${fmt(e.last_calibration.output_lpha, 0)} L/ha${e.last_calibration.by ? ` · ${e.last_calibration.by}` : ''}` : 'Never calibrated'])}
        />
      )}

      <Heading>Chemical stock</Heading>
      {s.chemical_stock.length === 0 ? <Empty>No chemical stock recorded.</Empty> : (
        <Table
          head={['Product', 'Unit', 'Opening', 'Received', 'Used', 'Adjusted', 'Disposed', 'Closing', 'Last stocktake']}
          rows={s.chemical_stock.map(r => [r.product_name, r.unit, fmt(r.opening, 2), fmt(r.received, 2), fmt(r.used, 2),
            fmt(r.adjusted, 2), fmt(r.disposed, 2), fmt(r.closing, 2), date(r.last_stocktake)])}
        />
      )}
    </div>
  );
}

function PeopleView({ s, tz }: { s: PeopleSection; tz: string }) {
  const date = useDate(tz);
  return (
    <div>
      <Heading>Equipment inductions</Heading>
      {s.inductions.length === 0 ? <Empty>No induction sign-offs recorded.</Empty> : (
        <Table head={['Person', 'Induction', 'Basis', 'Signed off', 'By', 'Expires']}
          highlight={i => s.inductions[i].status_at_end === 'expired'}
          rows={s.inductions.map(r => [r.person, r.induction, r.basis.replace(/_/g, ' '), date(r.signed_off), r.signed_off_by ?? '—', r.expires_on ? date(r.expires_on) : 'No expiry'])} />
      )}
      <Heading>Induction pack</Heading>
      {s.policies.length === 0 ? <Empty>No policy is in the induction pack.</Empty> : (
        <Table head={['Policy', 'Version', 'Applies to', 'Acknowledged', 'Outstanding']}
          highlight={i => s.policies[i].outstanding > 0}
          rows={s.policies.map(p => [p.title, p.version, p.audience, `${p.acknowledged} of ${p.audience_count}`, p.outstanding])} />
      )}
      <Heading>Certifications and licences</Heading>
      {s.certifications.length === 0 ? <Empty>No certifications recorded.</Empty> : (
        <Table head={['Person', 'Certification', 'Number', 'Issuer', 'Expiry']}
          highlight={i => s.certifications[i].status_at_end === 'expired'}
          rows={s.certifications.map(c => [c.person, c.type, c.number ?? '—', c.issuer ?? '—', c.expiry_date ? date(c.expiry_date) : 'No expiry'])} />
      )}
    </div>
  );
}

function WhsView({ s, tz, end }: { s: WhsSection; tz: string; end: string }) {
  const date = useDate(tz);
  return (
    <div>
      <Heading>Policies and procedures</Heading>
      {s.documents.length === 0 ? <Empty>None recorded.</Empty> : (
        <Table head={['Title', 'Type', 'Status', 'Review by']}
          highlight={i => !!s.documents[i].review_date && s.documents[i].review_date! < end}
          rows={s.documents.map(d => [d.title, d.doc_type, d.status, date(d.review_date)])} />
      )}
      <Heading>Risk assessments</Heading>
      {s.risk_assessments.length === 0 ? <Empty>No active risk assessments.</Empty> : (
        <Table head={['Title', 'Type', 'Assessed', 'Review by', 'Hazards (high residual)']}
          highlight={i => !!s.risk_assessments[i].review_date && s.risk_assessments[i].review_date! < end}
          rows={s.risk_assessments.map(r => [r.title, r.assessment_type, date(r.assessment_date), date(r.review_date), `${r.hazards} (${r.high_residual})`])} />
      )}
      <Heading>Incidents reported in the period</Heading>
      <p className="mb-2 text-xs text-neutral-500">Reporters, people involved and injury details are not included.</p>
      {s.incidents.length === 0 ? <Empty>No incidents reported in the period.</Empty> : (
        <Table head={['Code', 'Type', 'Title', 'Occurred', 'Severity', 'Status', 'Actions']}
          rows={s.incidents.map(r => [r.tracking_code, r.report_type.replace(/_/g, ' '), r.title ?? '(withheld)',
            date(r.occurred_date ?? r.reported_date), r.severity ?? '—', r.status, r.actions])} />
      )}
      <Heading>Corrective and preventive actions</Heading>
      {s.actions.length === 0 ? <Empty>None in the period.</Empty> : (
        <Table head={['Action', 'Priority', 'Assigned to', 'Due', 'Status']}
          highlight={i => s.actions[i].overdue}
          rows={s.actions.map(a => [a.title, a.priority, a.assigned_to ?? '—', `${date(a.due_date)}${a.overdue ? ' (overdue)' : ''}`, a.status])} />
      )}
      <Heading>Audits in the last 12 months</Heading>
      {s.audits.length === 0 ? <Empty>None recorded.</Empty> : (
        <Table head={['Title', 'Type', 'Date', 'Status', 'Findings (open)']}
          rows={s.audits.map(a => [a.title, a.audit_type.replace(/_/g, ' '), date(a.audit_date), a.status, `${a.findings} (${a.open_findings})`])} />
      )}
    </div>
  );
}

function HarvestView({ s, tz }: { s: HarvestSection; tz: string }) {
  const date = useDate(tz);
  return (
    <div>
      <p className="text-sm text-neutral-700">
        {s.bins.total} bins picked: {s.bins.with_area} placed in a planting, {s.bins.tagged} tagged, {s.bins.loaded} on a load.
      </p>
      <Heading>Loads</Heading>
      {s.loads.length === 0 ? <Empty>No loads in the period.</Empty> : (
        <Table head={['Load', 'Dispatched', 'To', 'Bins', 'Plantings', 'Packouts']}
          rows={s.loads.map(l => [l.load, date(l.dispatched_date), l.destination ?? '—', l.bins, l.plantings ?? '—', l.packouts ?? '—'])} />
      )}
    </div>
  );
}

interface Props {
  report: ComplianceReport;
  checks: AutoChecks;
  /** Told when a section tab is opened (the auditor portal logs it). */
  onSectionViewed?: (key: string) => void;
}

export default function ComplianceReportView({ report, checks, onSectionViewed }: Props) {
  const [tab, setTab] = useState<TabKey>('summary');
  const tz = report.timezone;
  const date = useDate(tz);
  const s = report.sections;

  const tabs: TabItem<TabKey>[] = [{ key: 'summary', label: 'Summary', icon: <ListChecks className="h-4 w-4" /> }];
  if (s.spray) tabs.push({ key: 'spray', label: 'Spray', icon: <SprayCan className="h-4 w-4" /> });
  if (s.people) tabs.push({ key: 'people', label: 'Training', icon: <Users className="h-4 w-4" /> });
  if (s.whs) tabs.push({ key: 'whs', label: 'Safety', icon: <ShieldCheck className="h-4 w-4" /> });
  if (s.harvest) tabs.push({ key: 'harvest', label: 'Harvest', icon: <Apple className="h-4 w-4" /> });

  const change = (k: TabKey) => { setTab(k); onSectionViewed?.(k); };
  const keys = Object.keys(checks);

  return (
    <div>
      <p className="mb-3 text-sm text-neutral-600">
        {report.organisation} · {date(report.period.start)} – {date(report.period.end)}
        {report.farms ? ` · ${report.farms.join(', ')}` : ' · all farms'}
      </p>
      {report.pseudonymised && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          People are shown as “Person N” and credential numbers are masked. This view is not enough for a certification audit, which has to identify who applied each product.
        </p>
      )}
      <Tabs tabs={tabs} active={tab} onChange={change} className="mb-4" />

      {tab === 'summary' && (
        keys.length === 0 ? <Empty>Nothing to summarise.</Empty> : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {keys.map(k => (
              <li key={k} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-start sm:gap-3">
                <div className="shrink-0 sm:w-24"><CheckBadge state={checks[k].state} /></div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-900">{CHECK_LABELS[k] ?? k}</p>
                  <p className="break-words text-sm text-neutral-600">{checks[k].detail}</p>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
      {tab === 'spray' && s.spray && <SprayView s={s.spray} tz={tz} />}
      {tab === 'people' && s.people && <PeopleView s={s.people} tz={tz} />}
      {tab === 'whs' && s.whs && <WhsView s={s.whs} tz={tz} end={report.period.end} />}
      {tab === 'harvest' && s.harvest && <HarvestView s={s.harvest} tz={tz} />}
    </div>
  );
}
